import { messagesNow } from '@/i18n'
import { messages } from './messages'

export interface FieldRef {
  name: string
  lang: 'en' | null
}
export type ViolationTarget =
  | { kind: 'course'; field: FieldRef | null }
  | { kind: 'lesson'; lessonId: number; field: FieldRef | null }
  | {
      kind: 'block'
      lessonId: number
      blockId: number
      part: string | null
      field: FieldRef | null
    }
  /** Форма статьи — одна страница, id строк в `ref` статьи нет. */
  | { kind: 'article'; field: FieldRef; linkIndex: number | null }
  | { kind: 'image'; url: string }
  /** Маскот курса — `course/mascot:{id}/field:image`, только как место картинки. */
  | { kind: 'mascot'; mascotId: number }
  | { kind: 'unknown'; raw: string }

const ID = /^\d+$/
const IMAGE_PREFIX = 'image:'

const parseField = (seg: string | undefined): FieldRef | null => {
  if (!seg?.startsWith('field:')) return null
  const raw = seg.slice('field:'.length)
  return raw.endsWith('_en') ? { name: raw.slice(0, -3), lang: 'en' } : { name: raw, lang: null }
}

const idOf = (seg: string | undefined, prefix: string): number | null => {
  if (!seg?.startsWith(`${prefix}:`)) return null
  const v = seg.slice(prefix.length + 1)
  return ID.test(v) ? Number(v) : null
}

/** Часть блока — ПОСЛЕДНИЙ сегмент перед `field:` (у `element:…/table:1.2` это таблица). */
const partOf = (segs: string[]): string | null => {
  const inner = segs.filter((s) => !s.startsWith('field:'))
  const last = inner[inner.length - 1]
  return last ? last.split(':')[0] : null
}

/** `article/field:<col>[/link:N]` — N это номер ссылки внутри поля, с нуля. */
const parseArticle = (segs: string[], raw: string): ViolationTarget => {
  const field = parseField(segs[1])
  const link = segs.length === 3 ? idOf(segs[2], 'link') : null
  const shapeOk = segs.length === 2 || (segs.length === 3 && link !== null)
  return field?.name && shapeOk
    ? { kind: 'article', field, linkIndex: link }
    : { kind: 'unknown', raw }
}

const parseLesson = (segs: string[], raw: string): ViolationTarget => {
  const unknown: ViolationTarget = { kind: 'unknown', raw }
  const lessonId = idOf(segs[0], 'lesson')
  if (lessonId === null) return unknown
  const field = parseField(segs[segs.length - 1])
  if (segs.length === 2) return field ? { kind: 'lesson', lessonId, field } : unknown
  const blockId = idOf(segs[1], 'block')
  if (blockId === null) return unknown
  return { kind: 'block', lessonId, blockId, part: partOf(segs.slice(2)), field }
}

export const parseViolationRef = (ref: string): ViolationTarget => {
  if (ref.startsWith(IMAGE_PREFIX)) {
    return ref.length > IMAGE_PREFIX.length
      ? { kind: 'image', url: ref.slice(IMAGE_PREFIX.length) }
      : { kind: 'unknown', raw: ref }
  }
  const segs = ref.split('/')
  if (segs[0] === 'course') {
    const field = parseField(segs[segs.length - 1])
    const mascotId = segs.length === 3 ? idOf(segs[1], 'mascot') : null
    if (mascotId !== null && field) return { kind: 'mascot', mascotId }
    return segs.length === 2 && field ? { kind: 'course', field } : { kind: 'unknown', raw: ref }
  }
  if (segs[0] === 'article') return parseArticle(segs, ref)
  return parseLesson(segs, ref)
}

/** Словари строятся на языке интерфейса в момент вызова. */
const fieldMap = (): ReadonlyMap<string, string> => {
  const m = messagesNow(messages)
  return new Map([
    ['name', m.fieldName],
    ['description', m.fieldDescription],
    ['text', m.fieldText],
    ['title', m.fieldTitle],
    ['question', m.fieldQuestion],
    ['link', m.fieldLink],
    ['content', m.fieldContent],
    ['answer', m.fieldAnswer],
    ['explanation', m.fieldExplanation],
    ['banner', m.fieldBanner],
    ['style_references', m.fieldStyleReferences],
  ])
}
const partMap = (): ReadonlyMap<string, string> => {
  const m = messagesNow(messages)
  return new Map([
    ['option', m.partOption],
    ['pair', m.partPair],
    ['button', m.partButton],
    ['element', m.partElement],
    ['table', m.partTable],
    ['badge', m.partBadge],
  ])
}

/** `labels` — словарь колонок своей сущности (у статьи колонки другие); суффикс языка общий. */
export const fieldLabel = (f: FieldRef, labels?: ReadonlyMap<string, string>): string =>
  `${(labels ?? fieldMap()).get(f.name) ?? f.name}${f.lang === 'en' ? ' (EN)' : ''}`
export const partLabel = (p: string): string => partMap().get(p) ?? p
